/**
 * Production-edition identity binding.
 *
 * In the demo edition the active identity comes from the PersonaSwitcher (a picked
 * persona). In a real-auth ("production") app-mode the logged-in account must instead
 * BE its real `users` roster row — so org placement, manager (for the approval chain),
 * access role, cost center and clearance all come from the database, not from auth
 * metadata. This module is the pure mapping layer for that bind; the App bootstrap
 * resolves the row by email (see fetchRosterIdentityByEmail) and applies it.
 *
 * Designed as a seam: today the "gate" is Supabase email auth + this roster lookup;
 * a future SSO / role-claims provider can replace the lookup without touching the
 * mapping or the consumers.
 */
import type { CurrentUser } from '@/types/ppm'
import { isInternalSurface } from '@/config/productSurface'
import type { SsoJitMode } from '@/config/authConfig'

/** The identity-bearing slice of a `users` row — the columns that answer "who am I"
 *  and "where do I sit" in the production edition. */
export interface RosterIdentity {
  id: string
  name: string
  email: string | null
  initials: string | null
  title: string | null
  systemRole: string | null
  department: string | null
  costCenterCode: string | null
  clearance: string | null
  managerId: string | null
  primaryOrganizationId: string | null
}

/**
 * App-wide RBAC role derived from the seeded `users.system_role`. Mirrors
 * PersonaSwitcher.deriveAppRole, but keyed on the REAL column — in production the
 * session is authenticated, so RLS allows reading `system_role` (the demo can't,
 * which is why it falls back to a hardcoded id allowlist + a role override).
 */
export function deriveAppRoleFromSystemRole(systemRole: string | null | undefined): string {
  switch (systemRole) {
    case 'GlobalAdmin':   // platform master dev
    case 'OrgAdmin':      // customer top admin — same admin app-role; org reach differs via organizationId (below)
      return 'GlobalAdmin'
    case 'GlobalViewer':  // read-only across every context (tenant-wide)
    case 'ConfigAdmin':   // configuration context only
      // Pass through unchanged: useAuthorization.resolveSystemRole only
      // recognises these as SYSTEM roles when currentUser.role carries the
      // literal name. Folding them into the default below would silently
      // hand a read-only account PortfolioManager write rights.
      return systemRole
    case 'Staff':
      return 'Viewer'
    case 'Auditor':
      return 'Viewer' // read-only oversight
    default:
      return 'PortfolioManager' // Supervisor / Lead / Manager / unset
  }
}

/** How an authenticated account resolves against the roster (#116 SSO JIT). */
export type RosterBindOutcome = 'bind' | 'metadata_fallback' | 'deny' | 'provision'

/**
 * Decide what to do with an authenticated user once the roster lookup returns.
 * Pure: the App bootstrap passes the facts and acts on the outcome.
 *  - has a roster row → 'bind' (the real org identity — the normal path).
 *  - no row, NOT a product build → 'metadata_fallback' (internal/sandbox
 *    convenience: keep the auth-metadata identity so testers aren't blocked).
 *  - no row, product build, 'require_roster' → 'deny' (ITAR-safe default: only
 *    pre-provisioned people get in).
 *  - no row, product build, 'provision' → 'provision' (fork's JIT seam).
 */
export function resolveRosterBindOutcome(
  hasRosterRow: boolean,
  isProduct: boolean,
  jitMode: SsoJitMode,
): RosterBindOutcome {
  if (hasRosterRow) return 'bind'
  if (!isProduct) return 'metadata_fallback'
  return jitMode === 'provision' ? 'provision' : 'deny'
}

function initialsFrom(name: string): string {
  return (name || 'U')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Bind a real `users` roster row to the app's CurrentUser. The logged-in person IS
 * this org member: org/role/clearance/cost-center come straight from the DB. The
 * manager linkage (`managerId`) drives the approval chain and is applied separately
 * via the timesheet store's `currentEmployeeId`, not carried on CurrentUser.
 */
export function rosterRowToCurrentUser(row: RosterIdentity): CurrentUser {
  // GlobalAdmin (platform master dev) spans ALL orgs — organizationId=null means
  // "no org scope" — but ONLY on the internal surface; it must not exist on a
  // customer product build, so there we defensively fall back to org-scoped.
  // OrgAdmin (and everyone else) stays bound to their primary org; OrgAdmin's
  // single-org enforcement is the fork's org-scoped RLS. (role_model_globaladmin_orgadmin)
  const allOrg = row.systemRole === 'GlobalAdmin' && isInternalSurface()
  return {
    id: row.id,
    name: row.name,
    role: deriveAppRoleFromSystemRole(row.systemRole),
    organizationId: allOrg ? null : (row.primaryOrganizationId ?? null),
    organizationName: null,
    assignedPortfolios: [],
    costCenterCode: row.costCenterCode ?? '',
    clearance: (row.clearance ?? 'None') as CurrentUser['clearance'],
    department: row.department ?? 'General',
    initials: row.initials || initialsFrom(row.name),
  }
}
